import { BankOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type LocalModelSpecsFragment } from '../../../../api/fragments/LocalModelSpecs';
import {
    GetCompanyDocument,
    type GetCompanyQuery,
    type GetCompanyQueryVariables,
} from '../../../../api/queries/getCompany';
import { useGetVehicleQuery } from '../../../../api/queries/getVehicle';
import { type LocalVariantSettings, VehicleKind } from '../../../../api/types';
import CompanyCurrencyField from '../../../../components/fields/CompanyCurrencyField';
import EnergyConsumptionField from '../../../../components/fields/EnergyConsumptionField';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import SortableUploadField from '../../../../components/fields/SortableUploadField';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import { useVariantBankCodeMappingDrawer } from '../../../../components/fields/VariantBankCodeMappingDrawer';
import VehicleSelectField from '../../../../components/fields/VehicleSelectField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions, pdfDocumentExtension } from '../../../../utilities/extensions';
import { withCommasForNumberString } from '../../../../utilities/useFormats';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useVehicleBodyTypeOptions from '../../../../utilities/useVehicleBodyTypeOptions';
import useVehicleEngineTypeOptions from '../../../../utilities/useVehicleEngineTypeOptions';
import { engineDisplacementFormatAmount, powerOutputFormatAmount, techinicalFormatAmount } from './helper';
import { type FormValues } from './types';

export type AddVariantDetailsProps = {
    initialValues: FormValues;
    updated?: string;
    companyId?: string;
    offset?: string;
    disabled?: boolean;
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const AddVariantDetails = ({ initialValues, updated, companyId, offset, disabled = false }: AddVariantDetailsProps) => {
    const { t } = useTranslation('variant');
    const { yesNoSwitch } = useSystemSwitchData();
    const apolloClient = useApolloClient();
    const { transmissionType } = useSystemOptions();
    const drawer = useVariantBankCodeMappingDrawer();

    const technicalFormatter = useCallback((value, { userTyping }) => {
        if (!value) {
            return null;
        }

        return userTyping ? withCommasForNumberString(value) : techinicalFormatAmount(value);
    }, []);

    const powerOutputFormatter = useCallback((value, { userTyping }) => {
        if (!value) {
            return null;
        }

        return powerOutputFormatAmount(value);
    }, []);

    const engineDisplacementFormatter = useCallback((value, { userTyping }) => {
        if (!value) {
            return null;
        }

        return engineDisplacementFormatAmount(value);
    }, []);

    const { values, setFieldValue } = useFormikContext<LocalVariantSettings>();

    const bodyTypeOptions = useVehicleBodyTypeOptions();
    const engineTypeOptions = useVehicleEngineTypeOptions();
    const { data, loading } = useGetVehicleQuery({
        fetchPolicy: 'cache-and-network',
        variables: { id: values.modelId },
        skip: (initialValues.modelId === values.modelId || !values.modelId) && !!values.submodelId,
    });

    const { data: submodelData, loading: submodelLoading } = useGetVehicleQuery({
        fetchPolicy: 'cache-and-network',
        variables: { id: values.submodelId },
        skip: initialValues.submodelId === values.submodelId || !values.submodelId,
    });

    const modelSelected = useMemo(() => data?.vehicle as LocalModelSpecsFragment, [data?.vehicle]);

    const [company, setCompany] = useState(null);

    const numberOfSubmodels = useMemo(() => modelSelected?.submodels.count, [modelSelected]);

    const submodelSelected = useMemo(() => submodelData?.vehicle as LocalModelSpecsFragment, [submodelData?.vehicle]);

    const getCompany = useCallback(async () => {
        if (modelSelected || companyId) {
            const companyData = await apolloClient.query<GetCompanyQuery, GetCompanyQueryVariables>({
                query: GetCompanyDocument,
                variables: { id: modelSelected?.module?.companyId || companyId },
            });

            setCompany(companyData?.data?.company);
        }
    }, [apolloClient, modelSelected, companyId]);

    useEffect(() => {
        if (values.modelId) {
            getCompany();
        }
    }, [getCompany, modelSelected, values.modelId]);

    // for change in model field
    useEffect(() => {
        if (!modelSelected) {
            return;
        }

        const { submodels, bodyType } = modelSelected;
        if (!updated) {
            const submodelId = submodels?.count ? submodels?.items?.[0]?.id : null;
            setFieldValue('submodelId', submodelId);
        }

        if (isEmpty(initialValues.bodyType)) {
            setFieldValue('bodyType', bodyType);
        }
    }, [initialValues.bodyType, modelSelected, numberOfSubmodels, setFieldValue, updated]);

    // for change in submodel field
    useEffect(() => {
        if (submodelSelected && isEmpty(initialValues.bodyType)) {
            setFieldValue('bodyType', submodelSelected.bodyType);
        }
    }, [setFieldValue, numberOfSubmodels, submodelSelected, initialValues.submodelId, initialValues.bodyType]);

    const bankCodeButton = (
        <Button
            icon={<BankOutlined style={{ color: 'var(--ant-primary-color)' }} />}
            onClick={drawer.open}
            size="small"
            type="text"
        />
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('variant:cardTitles.mainDetails')}>
                <Panel key={t('variant:cardTitles.mainDetails')} header={t('variant:cardTitles.mainDetails')}>
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <VehicleSelectField
                                {...t('variant:fields.model', { returnObjects: true })}
                                autoSelected={!updated}
                                disabled={disabled}
                                name="modelId"
                                vehicleKind={VehicleKind.LocalModel}
                                required
                            />
                        </Col>
                        {values.submodelId || (numberOfSubmodels && numberOfSubmodels !== 0) ? (
                            <Col {...colSpan}>
                                <VehicleSelectField
                                    {...t('variant:fields.submodel', { returnObjects: true })}
                                    autoSelected={!updated}
                                    disabled={(!numberOfSubmodels && numberOfSubmodels === 0) || disabled}
                                    name="submodelId"
                                    parentModelId={values.modelId ? values.modelId : null}
                                    vehicleKind={VehicleKind.LocalModel}
                                    required
                                />
                            </Col>
                        ) : null}
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('variant:fields.name', { returnObjects: true })}
                                disabled={disabled}
                                name="name"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('variant:fields.identifier', { returnObjects: true })}
                                addonAfter={bankCodeButton}
                                disabled={disabled}
                                name="identifier"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('variant:fields.bodyType', { returnObjects: true })}
                                disabled={loading || submodelLoading || disabled}
                                name="bodyType"
                                options={bodyTypeOptions}
                                required
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('variant:fields.description', { returnObjects: true })}
                                disabled={disabled}
                                name="description"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.DatePickerField
                                {...t('variant:fields.modelYear', { returnObjects: true })}
                                disabled={disabled}
                                name="modelYear"
                                picker="year"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputNumberField
                                {...t('variant:fields.order', { returnObjects: true })}
                                disabled={disabled}
                                min={1}
                                name="order"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('variant:fields.isActive', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="isActive"
                                {...yesNoSwitch}
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <SortableUploadField
                                {...t('variant:fields.picture', { returnObjects: true })}
                                disabled={disabled}
                                extensions={allowedExtensions.image}
                                name="images"
                                tooltip={t('common:upload.recommendedSize', { size: '1920x1080px' })}
                            />
                        </Col>
                        {updated && (
                            <Col {...colSpan}>
                                <FormFields.DisplayFieldWithUTCOffset
                                    {...t('common:fields.updated', { returnObjects: true, offset })}
                                    value={updated}
                                />
                            </Col>
                        )}
                    </Row>
                </Panel>
            </CollapsibleWrapper>

            <CollapsibleWrapper defaultActiveKey={t('variant:cardTitles.vehiclePrice')}>
                <Panel key={t('variant:cardTitles.vehiclePrice')} header={t('variant:cardTitles.vehiclePrice')}>
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <CompanyCurrencyField
                                {...t('variant:fields.vehiclePrice', { returnObjects: true })}
                                company={company}
                                disabled={disabled}
                                name="vehiclePrice"
                                required
                            />
                        </Col>
                    </Row>
                </Panel>
            </CollapsibleWrapper>

            <CollapsibleWrapper defaultActiveKey={t('variant:cardTitles.technicalDetails')}>
                <Panel key={t('variant:cardTitles.technicalDetails')} header={t('variant:cardTitles.technicalDetails')}>
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.InputNumberField
                                {...t('variant:fields.seats', { returnObjects: true })}
                                disabled={disabled}
                                name="seats"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('variant:fields.transmission', { returnObjects: true })}
                                disabled={disabled}
                                name="transmission"
                                options={transmissionType}
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('variant:fields.appleCarPlay', { returnObjects: true })}
                                disabled={disabled}
                                name="appleCarPlay"
                                {...yesNoSwitch}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <EnergyConsumptionField
                                {...t('variant:fields.energyConsumption', { returnObjects: true })}
                                disabled={disabled}
                                name="energyConsumption"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputNumberField
                                {...t('variant:fields.powerOutput', { returnObjects: true })}
                                addonAfter={t('variant:addOn.powerOutput')}
                                disabled={disabled}
                                formatter={powerOutputFormatter}
                                name="powerOutput"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputNumberField
                                {...t('variant:fields.topSpeed', { returnObjects: true })}
                                addonAfter={t('variant:addOn.topSpeed')}
                                disabled={disabled}
                                formatter={technicalFormatter}
                                name="topSpeed"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('variant:fields.engineType', { returnObjects: true })}
                                disabled={disabled}
                                name="engineType"
                                options={engineTypeOptions}
                                required
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('variant:fields.engine', { returnObjects: true })}
                                disabled={disabled}
                                name="engine"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputNumberField
                                {...t('variant:fields.engineDisplacement', { returnObjects: true })}
                                addonAfter={t('variant:addOn.engineDisplacement')}
                                disabled={disabled}
                                formatter={engineDisplacementFormatter}
                                name="engineDisplacement"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <TranslatedTextAreaField
                                {...t('variant:fields.features', { returnObjects: true })}
                                disabled={disabled}
                                name="features"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <SingleUploadField
                                {...t('variant:fields.brochure', { returnObjects: true })}
                                disabled={disabled}
                                extensions={[...allowedExtensions.image, pdfDocumentExtension]}
                                name="brochure"
                            />
                        </Col>
                    </Row>
                </Panel>
            </CollapsibleWrapper>

            {drawer.render({ name: 'bankCodeMappings' })}
        </Space>
    );
};

export default AddVariantDetails;
